import React, { useMemo } from "react";
import {
  Button,
  Collapse,
  ConfigProvider,
  Descriptions,
  Flex,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  FileOutlined,
  PlayCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type {
  CatalogAttribute,
  CatalogDetail,
  CatalogGroup,
  DiffEntry,
  LinkKind,
  ProductAttachment,
  ProductLink,
} from "./types";
import { ATRIBUTOS_DE_SISTEMA } from "./system-attributes";
import { MiniaturaMedia } from "./MiniaturaMedia";
import { arbolesVisibles } from "./stores-con-categorias";
import {
  VINCULOS,
  abrirAdjunto,
  esLinkSeguro,
  etiquetaDeProducto,
  formatoTamano,
} from "./VinculosYAdjuntos";

const { Text } = Typography;

/**
 * La ficha de un producto **en modo vista**: la usan el detalle de un producto publicado y
 * la revisión de una versión pendiente.
 *
 * **Tiene la estructura del formulario, a propósito** (xcons-docs#401): las mismas
 * secciones, en el mismo orden, con la misma grilla y el mismo encabezado. Quien revisa un
 * producto lo encuentra donde lo cargó; con otra disposición tenía que buscar cada dato.
 * Lo único distinto es que el valor es texto y no un input.
 */

/** La grilla del form: 360px por campo, tantos como entren. */
const GRILLA: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, 360px)",
  columnGap: 24,
  rowGap: 16,
};

/** El encabezado de sección del form: 40px. */
const TEMA_SECCION = {
  components: { Collapse: { headerPadding: "9px 16px" } },
};

/** Los inputs de media del conjunto: el form no los ofrece, la galería los gobierna. */
const INPUTS_DE_MEDIA = new Set(["media_image", "gallery"]);

/** Las secciones del conjunto con los campos que muestra el form, y ni uno más. */
export const seccionesDelForm = (grupos: CatalogGroup[]) =>
  grupos
    .map((grupo) => ({
      ...grupo,
      attributes: grupo.attributes.filter(
        (a) =>
          a.visible &&
          !ATRIBUTOS_DE_SISTEMA.has(a.code) &&
          !INPUTS_DE_MEDIA.has(a.frontendInput ?? ""),
      ),
    }))
    .filter((g) => g.attributes.length);

/** El `—` de un campo vacío: se muestra, no se esconde. */
export const Vacio = () => <Text type="secondary">—</Text>;

/**
 * Un campo: el nombre arriba y el valor abajo, en el lugar donde el form tiene el input.
 *
 * Con `cambio`, el campo es parte de lo que la versión pendiente cambia: se marca, y lo
 * publicado se muestra tachado debajo. En un alta no se pasa, porque no hay nada publicado
 * contra lo que comparar.
 */
export const Campo: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  cambio?: DiffEntry;
  /**
   * El valor es uno creado desde la ficha y sin revisar (xcons-docs#490): aprobar el producto
   * también lo aprueba. Va aparte de `cambio` porque en un alta también se marca.
   */
  nuevoValor?: boolean;
}> = ({ label, required, children, cambio, nuevoValor }) => (
  <Flex
    vertical
    gap={4}
    style={
      cambio
        ? {
            background: "#fffbe6",
            boxShadow: "0 0 0 6px #fffbe6",
            borderRadius: 2,
          }
        : undefined
    }
  >
    <Space size={6}>
      <Text type="secondary">
        {label}
        {required && <Text type="danger"> *</Text>}
      </Text>
      {cambio && (
        <Tag color="gold" style={{ marginInlineEnd: 0 }}>
          Cambia
        </Tag>
      )}
      {nuevoValor && (
        <Tooltip title="Se creó desde esta ficha y nadie lo revisó: al aprobar el producto, también se aprueba el valor">
          <Tag color="gold" style={{ marginInlineEnd: 0 }}>
            Valor nuevo
          </Tag>
        </Tooltip>
      )}
      {cambio?.conflict && (
        <Tooltip title="Lo publicado cambió después de armarse esta versión">
          <WarningOutlined style={{ color: "#faad14" }} />
        </Tooltip>
      )}
    </Space>
    <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {children}
    </div>
    {cambio && (
      <Text type="secondary" style={{ fontSize: 12 }}>
        <Text delete type="secondary" style={{ fontSize: 12 }}>
          {cambio.before ?? "—"}
        </Text>{" "}
        (publicado)
      </Text>
    )}
    {/* Sin el valor sobre el que se armó, el aviso dice "cambió" sin decir desde qué. */}
    {cambio?.conflict && (
      <Text type="warning" style={{ fontSize: 12 }}>
        se armó sobre: {cambio.conflict.base ?? "—"}
      </Text>
    )}
  </Flex>
);

/** Un bloque fuera de las secciones del conjunto, con el mismo encabezado que ellas. */
export const Bloque: React.FC<{
  titulo: string;
  /** Algo junto al título, como el resumen de "Quién lo vende". */
  junto?: React.ReactNode;
  children: React.ReactNode;
}> = ({ titulo, junto, children }) => (
  <ConfigProvider theme={TEMA_SECCION}>
    <Collapse
      defaultActiveKey={["bloque"]}
      items={[
        {
          key: "bloque",
          label: (
            <EncabezadoDeSeccion titulo={titulo} junto={junto} />
          ),
          children,
        },
      ]}
    />
  </ConfigProvider>
);

/**
 * El título de la sección, sin marca de cambio: lo que cambia se marca en cada campo
 * (Diseño, 2026-10-01).
 */
const EncabezadoDeSeccion: React.FC<{
  titulo: string;
  junto?: React.ReactNode;
}> = ({ titulo, junto }) => (
  <Space>
    <Text strong>{titulo}</Text>
    {junto}
  </Space>
);

/**
 * Las secciones del conjunto más la de Imágenes y videos, que en el form va al final del
 * mismo bloque. **Arrancan todas abiertas**: en el form se cierran para cargar de a una, y
 * acá se lee —o se revisa— la ficha entera.
 *
 * La identidad (SKU Fabricante, SKU XCONS y Nombre) son las primeras celdas de la primera
 * sección, que es Datos principales, como en el form.
 *
 * `extras` son los bloques que no son del EAV —Adjuntos, Relacionados—: van **en el mismo
 * bloque**, después de Imágenes y videos. Categorías queda fuera, en su propio `Bloque`
 * (xcons-docs#479).
 */
export const SeccionesDeLaFicha: React.FC<{
  secciones: CatalogGroup[];
  identidad: React.ReactNode[];
  valor: (attribute: CatalogAttribute) => React.ReactNode;
  cambios?: Map<string, DiffEntry>;
  /** Los atributos cuyo valor es uno nuevo, sin revisar (xcons-docs#490). También en un alta. */
  nuevos?: Set<string>;
  imagenes: { contenido: React.ReactNode };
  extras?: Array<{
    key: string;
    titulo: string;
    contenido: React.ReactNode;
  }>;
}> = ({ secciones, identidad, valor, cambios, nuevos, imagenes, extras = [] }) => (
  <ConfigProvider theme={TEMA_SECCION}>
    <Collapse
      defaultActiveKey={[
        ...secciones.map((g) => String(g.id)),
        "imagenes-y-videos",
        ...extras.map((e) => e.key),
      ]}
      items={[
        ...secciones.map((grupo, i) => ({
          key: String(grupo.id),
          label: <EncabezadoDeSeccion titulo={grupo.name} />,
          children: (
            <div style={GRILLA}>
              {i === 0 && identidad}
              {grupo.attributes.map((attribute) => (
                <Campo
                  key={attribute.code}
                  label={attribute.label ?? attribute.code}
                  required={attribute.required}
                  cambio={cambios?.get(attribute.code)}
                  nuevoValor={nuevos?.has(attribute.code)}
                >
                  {valor(attribute)}
                </Campo>
              ))}
            </div>
          ),
        })),
        {
          key: "imagenes-y-videos",
          label: <EncabezadoDeSeccion titulo="Imágenes y videos" />,
          children: imagenes.contenido,
        },
        ...extras.map((e) => ({
          key: e.key,
          label: <EncabezadoDeSeccion titulo={e.titulo} />,
          children: e.contenido,
        })),
      ]}
    />
  </ConfigProvider>
);

/**
 * Lo que la versión cambia en un bloque que no es del EAV —categorías, vínculos,
 * adjuntos—: cada línea con lo que queda y lo publicado tachado. En un alta, sólo lo que
 * queda.
 */
export const CambiosDelBloque: React.FC<{
  entradas: DiffEntry[];
  alta: boolean;
}> = ({ entradas, alta }) => (
  <div style={GRILLA}>
    {entradas.map((e) => (
      <Campo key={e.code} label={e.label} cambio={alta ? undefined : e}>
        {e.after ?? <Vacio />}
      </Campo>
    ))}
  </div>
);

/** El texto de un HTML, sin interpretarlo: `DOMParser` no ejecuta scripts ni carga nada. */
const textoDeHtml = (html: string) =>
  (new DOMParser().parseFromString(html, "text/html").body.textContent ?? "")
    .replace(/\s+/g, " ")
    .trim();

/** El valor publicado de un campo: la etiqueta si es un select, el valor si no. */
export const valorPublicado = (
  producto: CatalogDetail,
  attribute: CatalogAttribute,
): React.ReactNode => {
  const etiqueta = producto.selectedLabels?.[attribute.code];
  const huerfanas = producto.orphanOptions?.[attribute.code] ?? [];
  const texto = etiqueta
    ? Array.isArray(etiqueta)
      ? etiqueta.join(", ")
      : etiqueta
    : null;
  if (huerfanas.length) {
    // El producto apunta a una opción que ya no existe. Mostrar el id crudo (`12957`)
    // lo hacía pasar por el valor; el storefront, en cambio, no muestra nada.
    return (
      <Space size={6} wrap>
        {texto}
        <Tooltip title="El producto tiene elegida una opción que ya se borró del atributo. En la tienda no se muestra nada. Se puede corregir eligiendo otra opción desde Editar.">
          <Text type="secondary" italic>
            {huerfanas.map((id) => `Opción borrada (#${id})`).join(", ")}
          </Text>
        </Tooltip>
      </Space>
    );
  }
  if (texto) return texto;

  const valor = producto.values?.[attribute.code];
  if (valor === null || valor === undefined || valor === "") return <Vacio />;
  if (attribute.frontendInput === "boolean") {
    return Number(valor) === 1 ? "Sí" : "No";
  }
  // Magento devuelve los `decimal` con la escala de la columna: `0.054000` es `0.054`.
  if (
    attribute.frontendInput === "weight" ||
    attribute.frontendInput === "price"
  ) {
    const numero = Number(valor);
    if (Number.isFinite(numero)) return String(numero);
  }
  // Las descripciones vienen con el HTML de PageBuilder (`<div data-content-type=…>`):
  // se muestra el texto. Un bloque vacío es un campo vacío, no un `<div>` en pantalla.
  if (attribute.frontendInput === "textarea" && String(valor).includes("<")) {
    const limpio = textoDeHtml(String(valor));
    return limpio ? limpio : <Vacio />;
  }
  return String(valor);
};

type Media = NonNullable<CatalogDetail["media"]>[number];

/**
 * Una imagen que la edición **saca** del producto (xcons-docs#499): se sigue viendo, con el
 * fondo y el marco amarillos de lo que cambia y translúcida, así se distingue de las que
 * quedan.
 */
export const ImagenQuitada: React.FC<{
  file: string;
  url?: string;
  size?: number;
}> = ({ file, url, size = 120 }) => (
  <Space
    direction="vertical"
    size={2}
    align="center"
    style={{
      padding: 4,
      background: "#fffbe6",
      border: "1px solid #fadb14",
      borderRadius: 4,
    }}
  >
    <div style={{ opacity: 0.4 }}>
      <MiniaturaMedia file={file} url={url} size={size} />
    </div>
    <Tag color="gold" style={{ marginInlineEnd: 0 }}>
      Se quita
    </Tag>
  </Space>
);

/** Imágenes y videos, el contenido de la última sección. No son del EAV. */
export const ImagenesYVideos: React.FC<{
  media: Array<Pick<Media, "file"> & Partial<Media>>;
  /** Las que lo publicado tiene y la versión saca: van al lado de las que quedan. */
  quitadas?: Array<Pick<Media, "file"> & Partial<Media>>;
}> = ({ media, quitadas = [] }) => {
  // La galería guarda imágenes y videos juntos: el `file` de un video es su miniatura.
  const imagenes = media.filter((m) => m.mediaType !== "external-video");
  const videos = media.filter((m) => m.mediaType === "external-video");

  return (
    <Flex vertical gap={16}>
      <Campo label="Imágenes">
        {imagenes.length || quitadas.length ? (
          <Space wrap size={12} align="start">
            {imagenes.map((img, i) => (
              <Space
                key={img.file}
                direction="vertical"
                size={2}
                align="center"
              >
                <MiniaturaMedia file={img.file} url={img.url} size={120} />
                {/* Sin números: la primera es la principal, como en el form
                    (xcons-docs#479). */}
                {(i === 0 || img.disabled) && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {[i === 0 && "Principal", img.disabled && "Oculta"]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                )}
              </Space>
            ))}
            {quitadas.map((img) => (
              <ImagenQuitada key={img.file} file={img.file} url={img.url} />
            ))}
          </Space>
        ) : (
          <Vacio />
        )}
      </Campo>
      <Campo label="Videos">
        {videos.length ? (
          <Flex vertical gap={4}>
            {videos.map((v) => (
              <Space key={v.file} size={6}>
                <PlayCircleOutlined />
                {esLinkSeguro(v.video?.url) ? (
                  <a href={v.video!.url!} target="_blank" rel="noreferrer">
                    {v.video!.title || v.video!.url}
                  </a>
                ) : (
                  <Text>{v.video?.title ?? "(video sin link)"}</Text>
                )}
              </Space>
            ))}
          </Flex>
        ) : (
          <Vacio />
        )}
      </Campo>
    </Flex>
  );
};

/** Cuántos stores de un árbol se nombran antes de resumir: el de XCONS lo usan 33. */
const STORES_A_LA_VISTA = 4;

/**
 * El rótulo de un árbol de categorías: su root y los stores que lo usan. Con muchos se
 * resume, porque el de XCONS lo comparten 33 y la lista entera no dice nada de un vistazo.
 */
const EtiquetaDeArbol: React.FC<{
  nombre: string | null;
  stores: string[];
}> = ({ nombre, stores }) => {
  const aLaVista = stores.slice(0, STORES_A_LA_VISTA);
  const resto = stores.length - aLaVista.length;

  return (
    <Space direction="vertical" size={0}>
      <Text strong>{nombre ?? "(sin nombre)"}</Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {stores.length === 0 ? (
          "ningún store usa este árbol"
        ) : (
          <>
            {aLaVista.join(", ")}
            {resto > 0 && (
              <Tooltip title={stores.join(", ")}>{` y ${resto} más`}</Tooltip>
            )}
          </>
        )}
      </Text>
    </Space>
  );
};

/**
 * Las categorías, **una fila por árbol**, como pide el Excel de Maxi (XCONS y cada
 * [STORE]): en una lista plana `Productos` salía dos veces —una por árbol— sin forma de
 * saber de cuál era cada una. Primero los árboles con más stores. Las que no caen en
 * ninguno (`Root Catalog`) no se muestran (xcons-docs#487).
 *
 * Con `stores`, sólo los árboles que usa alguno de esos stores: la ficha pasa los stores
 * donde quien mira tiene **Categorías**, igual que el form, que tampoco ofrece los demás.
 * Las categorías de un árbol ajeno no se tocan: el producto las sigue teniendo, y el guardado
 * no las quita porque la escritura se acota al alcance de quien guarda.
 */
export const CategoriasPorArbol: React.FC<{
  categories: NonNullable<CatalogDetail["categories"]>;
  categoryRoots: NonNullable<CatalogDetail["categoryRoots"]>;
  stores?: string[];
}> = ({ categories, categoryRoots, stores }) => {
  const filas = useMemo(() => {
    const roots = [
      ...(stores ? arbolesVisibles(categoryRoots, stores) : categoryRoots),
    ].sort((a, b) => b.stores.length - a.stores.length);
    const lista: Array<{
      key: number | string;
      label: React.ReactNode;
      categorias: typeof categories;
    }> = roots.map((root) => ({
      key: root.rootCategoryId,
      label: <EtiquetaDeArbol nombre={root.name} stores={root.stores} />,
      categorias: categories.filter(
        (c) => c.rootCategoryId === root.rootCategoryId,
      ),
    }));

    // Las asignadas a Root Catalog, o a un árbol que no se resuelve, no se muestran
    // (xcons-docs#487): ningún store las muestra y a quien carga no le dicen nada. Siguen
    // guardadas en el producto.
    return lista.filter((f) => f.categorias.length);
  }, [categories, categoryRoots, stores]);

  if (!filas.length) {
    // "—" diría que el producto no tiene categorías, y puede tenerlas en árboles ajenos.
    return stores && categories.length ? (
      <Text type="secondary">
        No tenés el permiso <Text code>Categorías</Text> en las tiendas de este producto.
      </Text>
    ) : (
      <Vacio />
    );
  }

  return (
    <Descriptions
      bordered
      size="small"
      column={1}
      items={filas.map((fila) => ({
        key: String(fila.key),
        label: fila.label,
        children: (
          <Space wrap size={4}>
            {fila.categorias.map((c) => (
              <Tag key={c.categoryId}>{c.name ?? `#${c.categoryId}`}</Tag>
            ))}
          </Space>
        ),
      }))}
    />
  );
};

/**
 * "Productos relacionados" del Excel: los cuatro vínculos, cada uno con su significado. Una
 * clave que no viene —`complementary` con un API anterior a xcons-docs#476— se ve vacía.
 */
export const Relacionados: React.FC<{
  links?: Partial<Record<LinkKind, ProductLink[]>>;
}> = ({ links }) => {
  const navigate = useNavigate();
  return (
    <Flex vertical gap={16}>
      {VINCULOS.map(({ kind, label }) => (
        <Campo key={kind} label={label}>
          {links?.[kind]?.length ? (
            <Space wrap size={4}>
              {links[kind]!.map((p) => (
                <Tag
                  key={p.productId}
                  style={{ cursor: p.sku ? "pointer" : undefined }}
                  onClick={() =>
                    p.sku && navigate(`/catalog/${encodeURIComponent(p.sku)}`)
                  }
                >
                  {etiquetaDeProducto(p)}
                </Tag>
              ))}
            </Space>
          ) : (
            <Vacio />
          )}
        </Campo>
      ))}
    </Flex>
  );
};

export const cantidadDeVinculos = (
  links?: Partial<Record<LinkKind, ProductLink[]>>,
) => VINCULOS.reduce((n, { kind }) => n + (links?.[kind]?.length ?? 0), 0);

/**
 * Las fichas técnicas de MageWorx asociadas. Con `onQuitar` es la lista del form: cada una
 * lleva su botón para sacarla del producto (xcons-docs#432).
 */
export const Adjuntos: React.FC<{
  attachments: ProductAttachment[];
  onQuitar?: (adjunto: ProductAttachment) => void;
}> = ({ attachments, onQuitar }) =>
  attachments.length ? (
    <Flex vertical gap={4}>
      {attachments.map((a) => {
        const nombre = a.name ?? `Adjunto #${a.attachmentId}`;
        return (
          <Space key={a.attachmentId} size={6} wrap>
            <FileOutlined />
            {/* Un archivo subido se abre por el API (no depende de que Magento sirva la
                media, y anda en local); un link, directo. Sin ninguno de los dos, el
                nombre igual dice qué ficha es. */}
            {a.hasFile ? (
              <Typography.Link
                onClick={() => void abrirAdjunto(a.attachmentId)}
              >
                {nombre}
              </Typography.Link>
            ) : esLinkSeguro(a.url) ? (
              <a href={a.url} target="_blank" rel="noreferrer">
                {nombre}
              </a>
            ) : (
              <Text>{nombre}</Text>
            )}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {[a.type, formatoTamano(a.size)].filter(Boolean).join(" · ")}
            </Text>
            {!a.isActive && <Tag>inactivo</Tag>}
            {onQuitar && (
              <Tooltip title="Quitar del producto">
                <Button
                  aria-label={`Quitar ${nombre}`}
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onQuitar(a)}
                />
              </Tooltip>
            )}
          </Space>
        );
      })}
    </Flex>
  ) : (
    <Vacio />
  );
